import React from 'react';
import { useParams } from 'react-router-dom';

import {
  Caption,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  HStack,
  Heading,
  NewButton,
  Text,
  VStack,
} from '@eluve/components';
import { formatDate } from '@eluve/date-utils';

import { Citation } from './CitationText';
import {
  getSourceTypeLabel,
  getSourceUrl,
  parseCitedTextData,
} from './citation';

interface CitationModalProps {
  citation: Citation;
  isOpen: boolean;
  onClose: () => void;
}

export const CitationModal: React.FC<CitationModalProps> = ({
  citation,
  isOpen,
  onClose,
}) => {
  const { tenantId, patientId } = useParams<{
    tenantId: string;
    patientId: string;
  }>();
  const jsonData = parseCitedTextData(citation.text);
  const sourceType = citation.source.sourceType;
  const sourceLabel = getSourceTypeLabel(sourceType);

  // Get link to the original source page/document if available
  const sourceUrl = getSourceUrl(citation, tenantId, patientId);

  const handleViewOriginal = () => {
    if (sourceUrl) {
      window.open(sourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <Heading size="lg" weight="semibold">
              Citation Details - {sourceLabel}
            </Heading>
          </DialogTitle>
        </DialogHeader>

        <VStack gap={4} className="min-h-0 flex-1 overflow-y-auto">
          {/* Metadata */}
          <VStack gap={2}>
            <HStack className="grid grid-cols-2 gap-4">
              <HStack>
                <Caption size="sm" weight="bold" color="supporting">
                  Source Type:{' '}
                </Caption>
                <Text size="md" color="primary">
                  {sourceLabel}
                </Text>
              </HStack>
              {citation.source.sourceDocId && (
                <HStack>
                  <Caption size="sm" weight="bold" color="supporting">
                    Document ID:{' '}
                  </Caption>
                  <Text size="md" color="primary">
                    {citation.source.sourceDocId}
                  </Text>
                </HStack>
              )}
            </HStack>

            {formatDate.toLocale(citation.source.sourceTimestamp) && (
              <HStack>
                <Caption size="sm" weight="bold" color="supporting">
                  Created:{' '}
                </Caption>
                <Text size="md" color="primary">
                  {formatDate.toLocale(citation.source.sourceTimestamp)}
                </Text>
              </HStack>
            )}

            <VStack>
              <HStack>
                <Caption size="sm" weight="bold" color="supporting">
                  Position:{' '}
                </Caption>
                <Text size="md" color="primary">
                  Characters {citation.startIndex}-{citation.endIndex}
                </Text>
              </HStack>
              {citation.source.externalUrl && (
                <HStack>
                  <Caption size="sm" weight="bold" color="supporting">
                    External URL:{' '}
                  </Caption>
                  <Text size="md" color="primary" className="truncate">
                    {citation.source.externalUrl}
                  </Text>
                </HStack>
              )}
            </VStack>
          </VStack>

          {/* Content */}
          <VStack gap={3}>
            <Heading size="md" weight="semibold">
              Original Source Record
            </Heading>

            {jsonData ? (
              <div className="w-full overflow-auto rounded-lg bg-bg-gray-subtle p-4 font-mono text-sm">
                <pre>{JSON.stringify(jsonData, null, 2)}</pre>
              </div>
            ) : (
              <div className="rounded-lg border bg-bg-gray-subtle p-4">
                <Text size="md" color="primary" className="whitespace-pre-wrap">
                  {citation.text}
                </Text>
              </div>
            )}
          </VStack>
        </VStack>

        <HStack className="flex shrink-0 justify-between border-t pt-4">
          {sourceUrl ? (
            <NewButton
              text="View Source"
              type="primary"
              size="m"
              icon={{ name: 'ExternalLink' }}
              onClick={handleViewOriginal}
            />
          ) : (
            <div />
          )}
          <NewButton text="Close" type="outline" size="m" onClick={onClose} />
        </HStack>
      </DialogContent>
    </Dialog>
  );
};
